export type ErrorCategory =
  | 'VARIANT_NOT_FOUND'
  | 'NON_MUTABLE_ITEM'
  | 'STALE_QUANTITY'
  | 'LOCATION_INACTIVE'
  | 'RATE_LIMITED'
  | 'PERMISSION_DENIED'
  | 'UNKNOWN';

export interface SyncMetadata {
  status?: string;
  attemptCount?: number;
  lastAttempt?: string | null;
  errorCategory?: ErrorCategory | string | null;
  customerMessage?: string | null;
  supportDetail?: string | null;
}

export interface PartialRetryResponse {
  syncStatus: string;
  successful?: number;
  failed?: number;
  errorCategory?: ErrorCategory;
  customerMessage?: string | null;
  supportDetail?: string | null;
  /** AS-11067: one entry per received line; terminal ones carry `skipped: true`. */
  lines?: unknown[];
}

const DEFAULT_FALLBACK = 'Retry failed — please contact support.';

const CATEGORY_COLOURS: Record<ErrorCategory, string> = {
  VARIANT_NOT_FOUND: '#F44336',
  NON_MUTABLE_ITEM: '#F44336',
  PERMISSION_DENIED: '#F44336',
  LOCATION_INACTIVE: '#f57c00',
  RATE_LIMITED: '#FDD835',
  STALE_QUANTITY: '#FDD835',
  UNKNOWN: '#9E9E9E',
};

const CATEGORY_LABELS: Record<ErrorCategory, string> = {
  VARIANT_NOT_FOUND: 'Variant not found',
  NON_MUTABLE_ITEM: "Can't adjust in Shopify",
  STALE_QUANTITY: 'Stock changed',
  LOCATION_INACTIVE: 'Location inactive',
  RATE_LIMITED: 'Rate limited',
  PERMISSION_DENIED: 'Permission denied',
  UNKNOWN: 'Unknown',
};

// warbler's axios transformResponse leaves an ERROR body as the raw string
// (success bodies are parsed by http.ts), so parse it before reading fields.
function readErrorBody(error: any): any {
  const data = error?.response?.data;
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
}

// Precedence: classified customerMessage > server message > axios message > fallback.
export function extractRetryErrorMessage(error: any, fallback: string = DEFAULT_FALLBACK): string {
  const body = readErrorBody(error);
  return (
    body?.data?.customerMessage ||
    body?.message ||
    error?.message ||
    fallback
  );
}

/**
 * AS-11066: categories where the variant on the line is the problem, so
 * pointing the line at another variant can fix it — the variant is gone, or
 * Shopify refuses stock changes for it (a bundle / fulfillment-service item
 * picked by mistake). Every other category is about the store, not the line.
 */
export function canRelinkSyncFailure(category: string | null | undefined): boolean {
  return category === 'VARIANT_NOT_FOUND' || category === 'NON_MUTABLE_ITEM';
}

/**
 * Shown when a failure has no classified customerMessage. Without a category
 * we can't know a retry helps, so this must not promise it does (AS-11060:
 * "Try Retry Sync again" produced 10 attempts on one permanently failing receive).
 */
export function syncFailureFallbackMessage(status: string | null | undefined): string {
  return status === 'partial'
    ? 'Sync partially succeeded — some items failed. Check each failed item for what to do next, or contact support through live chat.'
    : 'Sync failed. Check each failed item for what to do next, or contact support through live chat.';
}

export interface SkippedRetryLine {
  receivedItemId: number;
  sku: string | null;
  errorCategory: string | null;
  customerMessage: string | null;
}

export interface TerminalRetry {
  message: string;
  skipped: SkippedRetryLine[];
}

const nonEmpty = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** The lines an AS-11067 retry deliberately did not re-send. */
export function skippedRetryLines(data: { lines?: unknown } | null | undefined): SkippedRetryLine[] {
  const lines = data?.lines;
  if (!Array.isArray(lines)) return [];
  const out: SkippedRetryLine[] = [];
  lines.forEach((line: any) => {
    if (!line || typeof line !== 'object' || line.skipped !== true) return;
    const id = Number(line.receivedItemId);
    if (line.receivedItemId == null || !Number.isSafeInteger(id)) return;
    out.push({
      receivedItemId: id,
      sku: nonEmpty(line.sku),
      errorCategory: nonEmpty(line.errorCategory),
      customerMessage: nonEmpty(line.customerMessage),
    });
  });
  return out;
}

/**
 * AS-11067: a 409 with `data.syncStatus: "terminal"` means every remaining
 * line is terminal and NOTHING was sent to Shopify. Null for any other failure.
 */
export function readTerminalRetry(error: any): TerminalRetry | null {
  if (error?.response?.status !== 409) return null;
  const body = readErrorBody(error);
  if (body?.data?.syncStatus !== 'terminal') return null;
  return {
    message: nonEmpty(body.data.customerMessage) || nonEmpty(body.message) || DEFAULT_FALLBACK,
    skipped: skippedRetryLines(body.data),
  };
}

export function describePartialRetry(response: PartialRetryResponse): { message: string; color: 'warning' } {
  const countedFallback = `${response.failed ?? 0} item${response.failed === 1 ? '' : 's'} failed to sync. Please check the Receipt Details.`;
  return {
    message: response.customerMessage || countedFallback,
    color: 'warning',
  };
}

export function getCategoryChipColor(category: string | null | undefined): string {
  if (!category) return CATEGORY_COLOURS.UNKNOWN;
  return CATEGORY_COLOURS[category as ErrorCategory] || CATEGORY_COLOURS.UNKNOWN;
}

export function formatCategoryLabel(category: string | null | undefined): string {
  if (!category) return CATEGORY_LABELS.UNKNOWN;
  return CATEGORY_LABELS[category as ErrorCategory] || CATEGORY_LABELS.UNKNOWN;
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export function shouldShowContactSupportBanner(
  metadata: SyncMetadata | null | undefined,
  nowMs: number = Date.now()
): boolean {
  if (!metadata) return false;
  if (metadata.status === 'synced') return false;
  if ((metadata.attemptCount ?? 0) < 3) return false;
  if (!metadata.lastAttempt) return false;

  const lastMs = new Date(metadata.lastAttempt).getTime();
  if (isNaN(lastMs)) return false;
  if (nowMs - lastMs > TWENTY_FOUR_HOURS_MS) return false;

  return true;
}

const DEFAULT_VARIANT_TITLES = new Set(['', 'default title', 'default']);

export function resolveItemLabel(
  item: { purchaseOrderItemId?: number | null; inventoryItemId?: number | null } | null | undefined,
  poItems: Array<{ id?: number | null; productName?: string | null; variant?: string | null; sku?: string | null }> | null | undefined
): string {
  if (!item) return 'Unknown item';

  const po = (poItems || []).find((p) => p && p.id === item.purchaseOrderItemId);
  if (po) {
    const name = (po.productName || '').trim();
    const variant = (po.variant || '').trim();
    const sku = (po.sku || '').trim();
    const hasMeaningfulVariant = variant && !DEFAULT_VARIANT_TITLES.has(variant.toLowerCase());
    const base = name || 'Unknown product';
    const labelParts = [hasMeaningfulVariant ? `${base} — ${variant}` : base];
    if (sku) labelParts.push(`(SKU: ${sku})`);
    return labelParts.join(' ');
  }

  if (item.inventoryItemId != null) {
    return `Inventory item #${item.inventoryItemId}`;
  }

  return 'Unknown item';
}

export function resolveLocationName(
  locationId: number | string | null | undefined,
  locations: Array<{ id?: number | string | null; name?: string | null }> | null | undefined
): string {
  if (locationId == null) return 'Unknown location';
  const numericId = typeof locationId === 'string' ? Number(locationId) : locationId;
  const match = (locations || []).find((l) => {
    if (!l || l.id == null) return false;
    const lid = typeof l.id === 'string' ? Number(l.id) : l.id;
    return lid === numericId;
  });
  return match?.name?.trim() || `Location #${locationId}`;
}

export interface FailedItemRow {
  label: string;
  categoryLabel: string;
  categoryColor: string;
  customerMessage: string;
  supportDetail: string | null;
  // Carried so a per-row action (e.g. Relink, US-066) can post back to the
  // server with the right primary keys without re-resolving them in the view.
  errorCategory: string | null;
  purchaseOrderItem: POItemLike | null;
  receivedItem: ReceivedItemLike;
}

export interface ReceivedItemLike {
  purchaseOrderItemId?: number | null;
  inventoryItemId?: number | null;
  shopifyInventorySyncMetadata?: SyncMetadata | null;
  shopifyCostSyncMetadata?: SyncMetadata | null;
}

export interface POItemLike {
  id?: number | null;
  productName?: string | null;
  variant?: string | null;
  sku?: string | null;
}

export function buildFailedItemRows(
  receivedItems: ReceivedItemLike[] | null | undefined,
  syncType: 'inventory' | 'cost',
  poItems: POItemLike[] | null | undefined
): FailedItemRow[] {
  if (!receivedItems) return [];

  const metadataField: keyof ReceivedItemLike = syncType === 'inventory'
    ? 'shopifyInventorySyncMetadata'
    : 'shopifyCostSyncMetadata';

  // Index PO items by id once so resolveItemLabel is O(1) per row instead of O(M).
  const poItemIndex = new Map<number, POItemLike>();
  for (const p of poItems || []) {
    if (p && p.id != null) poItemIndex.set(p.id, p);
  }

  const rows: FailedItemRow[] = [];
  for (const item of receivedItems) {
    const metadata = item?.[metadataField] as SyncMetadata | null | undefined;
    if (!metadata) continue;
    if (metadata.status !== 'failed' && metadata.status !== 'partial') continue;

    // Render a row for every failed/partial item, even when no narrative text
    // is set: the chip + label alone is still useful diagnostic info, and
    // dropping the row would silently hide a real failure.
    const fallback = metadata.status === 'partial'
      ? 'Partially synced — see details.'
      : 'Sync failed — see details.';
    const customerMessage = metadata.customerMessage
      || (metadata as { error?: string | null }).error
      || fallback;

    const po = item?.purchaseOrderItemId != null ? poItemIndex.get(item.purchaseOrderItemId) : undefined;
    rows.push({
      label: po ? resolveItemLabel(item, [po]) : resolveItemLabel(item, []),
      categoryLabel: formatCategoryLabel(metadata.errorCategory),
      categoryColor: getCategoryChipColor(metadata.errorCategory),
      customerMessage,
      supportDetail: metadata.supportDetail || null,
      errorCategory: (metadata.errorCategory as string) || null,
      purchaseOrderItem: po ?? null,
      receivedItem: item,
    });
  }
  return rows;
}

export interface RunRetryOptions<T> {
  /** The retry network call. */
  call: () => Promise<T>;
  /** Toast shown on non-partial success. */
  successMessage: string;
  /** Toast shown on network / server error; defaults to `extractRetryErrorMessage`. */
  fallbackMessage?: string;
  /** Called before/after the network call to toggle a loading flag. */
  setLoading: (v: boolean) => void;
  /** Snackbar emitter (e.g. `(message, color) => EventBus.$emit('show-snackbar', { message, color })`). */
  emit: (message: string, color: 'success' | 'warning' | 'error') => void;
  /** Called on every success path (partial or full) before the toast is emitted. */
  onSuccess?: (response: T & Partial<PartialRetryResponse>) => void | Promise<void>;
  /** Called after the toast — typical place to refresh data. */
  afterSettle?: () => void | Promise<void>;
  /** AS-11687: a 409 terminal answer — nothing was sent. Shown as a warning, not an error. */
  onTerminal?: (terminal: TerminalRetry) => void;
  /** AS-11687: lines a partial retry skipped as terminal. */
  onSkipped?: (lines: SkippedRetryLine[]) => void;
  /**
   * AS-11687: false once the merchant has moved to another receipt, so a late
   * answer cannot write its terminal / skipped outcome onto the new one.
   */
  isCurrent?: () => boolean;
}

export async function runRetry<T>(opts: RunRetryOptions<T>): Promise<void> {
  opts.setLoading(true);
  try {
    const response = await opts.call();
    const partial = (response as any)?.syncStatus === 'partial';
    if (opts.onSuccess) await opts.onSuccess(response as any);
    const current = !opts.isCurrent || opts.isCurrent();
    if (opts.onSkipped && current) opts.onSkipped(skippedRetryLines(response as any));
    if (partial) {
      const { message, color } = describePartialRetry(response as any);
      opts.emit(message, color);
    } else {
      opts.emit(opts.successMessage, 'success');
    }
  } catch (error: any) {
    const terminal = readTerminalRetry(error);
    if (terminal) {
      opts.emit(terminal.message, 'warning');
      if (opts.onTerminal && (!opts.isCurrent || opts.isCurrent())) opts.onTerminal(terminal);
    } else {
      opts.emit(extractRetryErrorMessage(error, opts.fallbackMessage), 'error');
    }
  } finally {
    // Always run afterSettle so attempt counters and failed chips reflect
    // the persisted state — even when the retry itself rejected.
    if (opts.afterSettle) {
      try {
        await opts.afterSettle();
      } catch (settleError) {
        console.error('[runRetry] afterSettle failed:', settleError);
      }
    }
    opts.setLoading(false);
  }
}

export interface DiagnosticInput {
  poNumber: string;
  receiveRecordId: string | number;
  merchantId: string | number;
  deliveryNoteNumber?: string | null;
  errorCategory?: string | null;
  attemptCount: number;
  lastAttempt: string | null;
  supportDetail?: string | null;
}

export function buildDiagnosticInfo(input: DiagnosticInput): string {
  const lines = [
    'Assisty retry-sync diagnostic info',
    '----------------------------------',
    `PO number: ${input.poNumber}`,
    `Receive record: ${input.receiveRecordId}`,
    `Merchant ID: ${input.merchantId}`,
  ];

  if (input.deliveryNoteNumber) {
    lines.push(`Delivery note: ${input.deliveryNoteNumber}`);
  }

  lines.push(`Error category: ${input.errorCategory || 'UNKNOWN'}`);
  lines.push(`Attempt count: ${input.attemptCount}`);
  lines.push(`Last attempt: ${input.lastAttempt || 'N/A'}`);

  if (input.supportDetail) {
    lines.push('');
    lines.push('Support detail:');
    lines.push(input.supportDetail);
  }

  return lines.join('\n');
}
